import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaRegBuilding } from "react-icons/fa";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { FiAlertTriangle, FiHome } from "react-icons/fi";
import PageHeader from '../../../components/ui/PageHeader';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { strings } from '../../../strings/strings';
import { ROUTES } from '../../../routes/routes';
import RecentActivity from './RecentActivity';
import QuickActions from './QuickActions';
import AdminLoginMonitoring from './AdminLoginMonitoring';
import { apiCall } from '../../../api/fetchClient';
import { GET_DASHBOARD_URL } from '../../../api/url';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    suspendedCompanies: 0,
  });
  const [loading, setLoading] = useState(true);
  const hasFetchedDashboard = useRef(false);
  const [monitoringData, setMonitoringData] = useState({})

  const handleStatCardClick = (status) => {
    navigate(ROUTES.ADMIN_COMPANY, { state: { status } });
  };

  useEffect(() => {
    if (hasFetchedDashboard.current) return;
    hasFetchedDashboard.current = true;

    const fetchDashboardData = async () => {
      try {
        const response = await apiCall(GET_DASHBOARD_URL, 'GET');
        if (!!response.success) {
          setStats({
            totalCompanies: response?.results?.companies?.total || 0,
            activeCompanies: response?.results?.companies?.active || 0,
            suspendedCompanies: response?.results?.companies?.suspended || 0,
          });
          setMonitoringData(response?.results?.superAdmin)
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const statCards = [
    {
      title: strings.TOTAL_COMPANIES,
      value: stats.totalCompanies,
      Icon: FaRegBuilding ,
      bgColor: "bg-blue-500",
      gradientColor: "from-blue-500 to-blue-600",
      iconColor: "text-blue-600",
      status: "all"
    },
    {
      title: strings.ACTIVE_COMPANIES,
      value: stats.activeCompanies,
      Icon: IoIosCheckmarkCircleOutline,
      bgColor: "bg-green-500",
      gradientColor: "from-green-500 to-green-600",
      iconColor: "text-green-600",
      status: "active"
    },
    {
      title: strings.SUSPENDED_COMPANIES,
      value: stats.suspendedCompanies,
      Icon: FiAlertTriangle,
      bgColor: "bg-red-500",
      gradientColor: "from-red-500 to-red-600",
      iconColor: "text-red-600",
      status: "suspended"
    },
  ];

  return (
    <div>
      <PageHeader
        title={strings.DASHBOARD}
        description={strings.DASHBOARD_DESCRIPTION}
        icon={FiHome}
      />

      {loading ? (
        <LoadingSpinner message="Loading dashboard..." minHeight="h-64" />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {statCards.map((card, index) => {
              const { Icon } = card;
              return (
                <div
                  key={index}
                  onClick={() => handleStatCardClick(card.status)}
                  className="group bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm p-6 hover:shadow-xl transition-all duration-200 hover:scale-[1.02] relative overflow-hidden cursor-pointer"
                >
                  {/* Background Gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${card.gradientColor} opacity-5 group-hover:opacity-10 transition-opacity duration-300`}></div>
                  
                  <div className="relative z-10 flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-gray-500 dark:text-gray-400 text-sm font-medium mb-2 uppercase tracking-wide">{card.title}</p>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{card.value}</p>
             
                    </div>
                    <div className={`${card.bgColor} bg-opacity-15 dark:bg-opacity-20 p-4 rounded-2xl group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className={`w-6 h-6 ${card.iconColor} dark:${card.iconColor.replace('text-', 'text-')}`} />
                    </div>
                  </div>
                  
                  {/* Subtle border accent */}
                  <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${card.gradientColor} opacity-20`}></div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
            <div className="lg:col-span-2 relative h-[500px] lg:h-auto">
              <div className="lg:absolute lg:inset-0 h-full">
                <RecentActivity />
              </div>
            </div>
            <div className="lg:col-span-1 space-y-6">
              <AdminLoginMonitoring data={monitoringData} />
              <QuickActions />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SuperAdminDashboard